/** Minimum length enforced by `password` in schemas/common.ts. */
export const PASSWORD_MIN_LENGTH = 8;

export type PasswordStrengthLevel = 'empty' | 'too_short' | 'easy' | 'medium' | 'strong';

export type PasswordStrength = {
  level: PasswordStrengthLevel;
  /** 0–100 for meter width */
  percent: number;
  label: string;
};

/**
 * Heuristic strength for live UI feedback only — server validation is length-based.
 * "Easy" vs "Strong" nudges users without composition rules in the schema.
 */
export function evaluatePasswordStrength(value: string): PasswordStrength {
  if (!value) {
    return { level: 'empty', percent: 0, label: '' };
  }

  if (value.length < PASSWORD_MIN_LENGTH) {
    return {
      level: 'too_short',
      percent: Math.max(8, (value.length / PASSWORD_MIN_LENGTH) * 35),
      label: `Too short — use at least ${PASSWORD_MIN_LENGTH} characters`,
    };
  }

  let variety = 0;
  if (/[a-z]/.test(value)) variety += 1;
  if (/[A-Z]/.test(value)) variety += 1;
  if (/[0-9]/.test(value)) variety += 1;
  if (/[^A-Za-z0-9]/.test(value)) variety += 1;

  const long = value.length >= 12;

  if (variety <= 1 && !long) {
    return { level: 'easy', percent: 42, label: 'Easy password — add numbers or symbols' };
  }

  if (variety >= 3 && (long || value.length >= 10)) {
    return { level: 'strong', percent: 100, label: 'Strong password' };
  }

  return { level: 'medium', percent: 72, label: 'Fair password — almost there' };
}
